<template>
    <page-container maxSize="2xl">
        <div class="grid grid-cols-1 gap-6 xl:grid-cols-[auto_repeat(11,1fr)] xl:grid-rows-[auto_1fr]">
            <div class="order-2 xl:order-0 xl:row-span-2 xl:w-64">
                <analytics-sidebar
                    v-model="fetchParams"
                    hideCategoryFilter
                    hideResourceFilter
                    @reset="resetFilters()"
                    :minAllowedDate="minAllowedDate"
                ></analytics-sidebar>
            </div>

            <analytics-franchise-hub-page-header
                homeHref=""
                backButtonHref="/analytics/franchise-hub"
                backButtonText="Back to Team"
                :fetchParams="debouncedFetchParams"
                :category="props.category"
            />

            <div class="order-3 w-full relative xl:col-span-11">
                <transition
                    enter-from-class="opacity-0 -translate-y-4"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-4"
                >
                    <div v-if="loading" class="absolute w-full transition pt-20">
                        <loading-bars></loading-bars>
                    </div>
                </transition>

                <div
                    v-if="debouncedFetchParams && debouncedFetchParamsWithCategory"
                    class="flex flex-col gap-6"
                    :class="{
                        'opacity-0 pointer-events-none': loading,
                        'opacity-100 delay-200 transition duration-150': !loading,
                    }"
                >
                    <div class="grid gap-6 grid-cols-1 sm:grid-cols-12">
                        <analytics-outbound-messages-total-active-resources-by-resource-type
                            resourceType="campaign"
                            :senderType="SENDER_TYPE_CORPORATE"
                            :fetchParams="debouncedFetchParamsWithCategory"
                            :backgroundIcon="faCrown"
                            href="/messaging/campaigns"
                            minHeight="128px"
                            class="col-span-12 md:col-span-3"
                        >
                            <template v-slot:title> Corporate Campaigns </template>
                        </analytics-outbound-messages-total-active-resources-by-resource-type>
                        <analytics-outbound-messages-total-active-resources-by-resource-type
                            resourceType="campaign"
                            :senderType="SENDER_TYPE_ORG_UNIT"
                            :fetchParams="debouncedFetchParamsWithCategory"
                            :backgroundIcon="faBuilding"
                            minHeight="128px"
                            class="col-span-12 md:col-span-3"
                        >
                            <template v-slot:title> Organizational Unit Campaigns </template>
                        </analytics-outbound-messages-total-active-resources-by-resource-type>
                        <analytics-outbound-messages-total-active-resources-by-resource-type
                            resourceType="campaign"
                            :senderType="SENDER_TYPE_GROUP"
                            :fetchParams="debouncedFetchParamsWithCategory"
                            :backgroundIcon="faMessages"
                            minHeight="128px"
                            class="col-span-12 md:col-span-3"
                        >
                            <template v-slot:title> Group Campaigns </template>
                        </analytics-outbound-messages-total-active-resources-by-resource-type>
                        <analytics-outbound-messages-total-active-groups
                            :fetchParams="debouncedFetchParamsWithCategory"
                            :href="`/settings/teams/${teamId}#/groups`"
                            :backgroundIcon="faGrid2Plus"
                            minHeight="128px"
                            class="col-span-12 md:col-span-3"
                        />
                    </div>

                    <analytics-outbound-messages-total-messages-sent-by-audience-type
                        :fetchParams="debouncedFetchParamsWithCategory"
                        class="col-span-12"
                    />

                    <div v-if="props.category !== 'none'" class="flex flex-col gap-6">
                        <div class="flex flex-col gap-4 pt-4 xl:justify-between xl:items-center xl:flex-row">
                            <span class="font-bold text-xl">Performance by Metric</span>
                            <analytics-performance-legend />
                        </div>

                        <div class="grid gap-6 grid-cols-1 xl:grid-cols-12">
                            <category-metric-card
                                v-for="metric in metricsWithGoals"
                                :key="metric"
                                :metric="metric"
                                :fetchParams="debouncedFetchParams"
                                :category="props.category"
                                class="col-span-1 xl:col-span-6"
                            />
                        </div>
                    </div>

                    <div class="flex flex-col gap-6">
                        <div class="flex flex-col gap-4 pt-4 xl:flex-row xl:justify-between xl:items-center">
                            <span class="font-bold text-xl">Resources in Category</span>
                        </div>

                        <analytics-outbound-messages-export-resource-metrics
                            :category="props.category"
                            :fetchParams="debouncedFetchParams"
                            :filename="`voxie-${props.category}-resources`"
                        />
                    </div>
                </div>
            </div>
        </div>
    </page-container>
</template>

<script setup>
import PageContainer from '~/components/general/PageContainer.vue';
import { faBuilding, faCrown, faGrid2Plus, faMessages } from '@fortawesome/pro-solid-svg-icons';
import AnalyticsSidebar from './AnalyticsSidebar.vue';
import { useDebouncedFetchParams } from '~/components/analytics/useDebouncedFetchParams';
import {
    getDateRange,
    getMinAllowedDate,
    resourceTypesOptions,
    metricsWithGoals,
    addCategoryToFetchParams,
} from '~/utils/analytics';
import { computed } from 'vue';
import { audienceTypes } from '~/constants/campaign';
import { analyticsCategoriesValues } from '~/constants/categories';
import { SENDER_TYPES, SENDER_TYPE_CORPORATE, SENDER_TYPE_ORG_UNIT, SENDER_TYPE_GROUP } from '~/constants/senderTypes';
import LoadingBars from '~/components/general/LoadingBars.vue';
import AnalyticsOutboundMessagesTotalMessagesSentByAudienceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalMessagesSentByAudienceType.vue';
import AnalyticsPerformanceLegend from '~/components/analytics/partial/AnalyticsPerformanceLegend.vue';
import CategoryMetricCard from '~/components/analytics/partial/category-metrics/CategoryMetricCard.vue';
import AnalyticsOutboundMessagesExportResourceMetrics from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesExportResourceMetrics.vue';
import AnalyticsOutboundMessagesTotalActiveResourcesByResourceType from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalActiveResourcesByResourceType.vue';
import AnalyticsOutboundMessagesTotalActiveGroups from '~/components/analytics/charts/time-series/AnalyticsOutboundMessagesTotalActiveGroups.vue';
import AnalyticsFranchiseHubPageHeader from '~/components/analytics/partial/AnalyticsFranchiseHubPageHeader.vue';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    category: {
        type: String,
        validator: (category) => analyticsCategoriesValues.includes(category),
        required: true,
    },
});

const defaultFilters = () => {
    const last30Days = getDateRange('last_30_days');

    return {
        range: [last30Days.start_date, last30Days.end_date],
        filters: {
            sender_types: SENDER_TYPES.map((senderType) => senderType.value),
            audience_types: audienceTypes().map((audienceType) => audienceType.name),
            resource_types: resourceTypesOptions.map((resourceType) => resourceType.value),
            resource_categories: undefined,
            resources: undefined,
        },
    };
};

const { fetchParams, loading, debouncedFetchParams, resetFilters } = useDebouncedFetchParams(defaultFilters());

const minAllowedDate = getMinAllowedDate(true);

const debouncedFetchParamsWithCategory = computed(() =>
    addCategoryToFetchParams(debouncedFetchParams.value, props.category)
);
</script>
